import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import {
  Button,
  Checkbox,
  FormGroup,
  Intent
} from "@blueprintjs/core";
import TechnicianPicker from "components/TechnicianPicker";
import JobPicker from "components/JobPicker";
import { observable, toJS } from 'mobx';
import Toast from "helpers/toaster";
import _ from 'lodash';
import { currencyFormattedAmount } from 'helpers/currency';
import FormDrawer from 'components/FormDrawer';
import { FormSection, Choice, FieldError, MoreOptions, GrowingTextArea } from 'components/FormBits';

// Create invoice for a job: pick which of the job's labor, materials and other
// charges go on it (or bill one flat-rate line), and see the total as you go.
// Totals are worked out from the current selection on every render.

const blankCustomLine = () => ({
  attributes: {
    description: '',
    total: '',
    tax_line_item_id: '',
    quantity: '',
    markup: '',
    rate_per_unit: '',
    tax_amount: ''
  }
});

const toNumber = (value) => {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : 0;
};

const GROUPS = [
  { key: 'labours', title: 'Labor', unit: 'h' },
  { key: 'materials', title: 'Materials', unit: '' },
  { key: 'customs', title: 'Other charges', unit: '' },
];

const styles = {
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 12,
    margin: '0 0 20px',
    padding: '12px 14px',
    borderRadius: 12,
    background: 'var(--fd-page)',
    border: '1px solid var(--fd-divider)',
    fontSize: 13,
    color: 'var(--fd-text)',
  },
  summaryName: { margin: 0, color: 'var(--fd-ink)', fontSize: 15, fontWeight: 800 },
  summaryLine: { margin: '2px 0 0' },
  groupTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '4px 0 6px',
    color: 'var(--fd-muted)',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  row: (checked) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px 12px',
    margin: '0 0 6px',
    padding: '10px 12px 0',
    border: `1px solid ${checked ? 'var(--fd-accent)' : 'var(--fd-line)'}`,
    borderRadius: 10,
    background: checked ? 'var(--fd-accent-tint)' : 'var(--fd-surface)',
  }),
  rowMain: { flex: '1 1 240px', minWidth: 0 },
  rowMeta: { display: 'block', marginTop: -6, paddingLeft: 26, paddingBottom: 10, color: 'var(--fd-muted)', fontSize: 12 },
  rowAmount: { flex: '0 0 110px', paddingBottom: 10, textAlign: 'right' },
  rowTotal: { display: 'block', color: 'var(--fd-ink)', fontWeight: 800 },
  rowTax: { display: 'block', color: 'var(--fd-muted)', fontSize: 12 },
  rowTaxSelect: { flex: '0 0 170px', paddingBottom: 10 },
  empty: { margin: '0 0 12px', padding: '14px', borderRadius: 10, background: 'var(--fd-page)', color: 'var(--fd-muted)', fontSize: 13 },
  totals: {
    margin: '8px 0 20px',
    padding: '12px 14px',
    borderRadius: 12,
    background: 'var(--fd-page)',
    border: '1px solid var(--fd-divider)',
  },
  totalRow: { display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: 'var(--fd-text)', fontSize: 14 },
  grandRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 6,
    paddingTop: 8,
    borderTop: '1px solid var(--fd-line)',
    color: 'var(--fd-ink)',
    fontSize: 18,
    fontWeight: 800,
  },
  flatGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0 12px' },
  linkButton: {
    padding: '2px 4px',
    border: 0,
    background: 'none',
    color: 'var(--fd-accent)',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },
};

@inject("viewStore", "invoiceStore","jobStore","taxStore")
@observer
class InvoiceJobModal extends Component {

  @observable selectedOptions = null;
  @observable job_detals = null;
  @observable flat_rate = false;
  // The single line billed when "Flat rate" is chosen.
  @observable customLine = blankCustomLine();
  @observable errors = {};

  componentDidMount() {
    let {
      viewStore: {
        currentUser
      },
      invoiceStore: {
        jobInvoice: {
          object
        }
      },
      job
    } = this.props;
    object.technician_id = [currentUser.id]
    if (!object.po_number && job && job.attributes && job.attributes.po_number) {
      object.po_number = job.attributes.po_number;
    }
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:hide']);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.errors = {};
      this.preselectCreatedBy();
      this.initializePoNumberFromJob();
    }
  }

  // The dialog stays mounted, and the form is reset after each invoice is
  // created, so default "Created by" to the current user on every open.
  preselectCreatedBy() {
    const { viewStore: { currentUser }, invoiceStore: { jobInvoice: { object } } } = this.props;
    if (!this.hasTechnician() && currentUser) {
      object.technician_id = [currentUser.id];
    }
  }

  initializePoNumberFromJob() {
    let {
      invoiceStore: {
        jobInvoice: { object }
      },
      job
    } = this.props;
    if (job && job.attributes && job.attributes.po_number) {
      object.po_number = job.attributes.po_number;
    }
  }

  get lineItems() {
    const { object } = this.props.jobStore.materialLineItems;
    return (object && object.line_items) || null;
  }

  // Items shown in each group (other charges without a price are left out, as before).
  visibleItems(key) {
    const lineItems = this.lineItems;
    const items = (lineItems && lineItems[key]) || [];
    return key === 'customs' ? items.filter((custom) => custom.attributes.rate_per_unit > 0) : items;
  }

  findTax(id) {
    const { objects } = this.props.taxStore.taxes;
    return id ? _.find(objects, { attributes: { id } }) : undefined;
  }

  // Subtotal, tax and total for what's selected right now.
  totals() {
    if (this.flat_rate) {
      const { quantity, rate_per_unit, tax_line_item_id } = this.customLine.attributes;
      const subTotal = toNumber(toNumber(quantity).toFixed(2)) * toNumber(toNumber(rate_per_unit).toFixed(2));
      const tax = this.findTax(tax_line_item_id);
      const totalTax = tax ? (toNumber(tax.attributes.percentage_value) / 100) * subTotal : 0;
      return { subTotal, totalTax, grandTotal: subTotal + totalTax, count: subTotal > 0 ? 1 : 0, available: 1 };
    }
    const lineItems = this.lineItems;
    let subTotal = 0;
    let totalTax = 0;
    let count = 0;
    GROUPS.forEach(({ key }) => {
      ((lineItems && lineItems[key]) || []).forEach((item) => {
        if (item.attributes.is_checked) {
          subTotal += toNumber(item.attributes.total);
          totalTax += toNumber(item.attributes.tax_amount);
        }
      });
      count += this.visibleItems(key).filter((item) => item.attributes.is_checked).length;
    });
    const available = GROUPS.reduce((sum, { key }) => sum + this.visibleItems(key).length, 0);
    return { subTotal, totalTax, grandTotal: subTotal + totalTax, count, available };
  }

  hasTechnician() {
    const { technician_id } = this.props.invoiceStore.jobInvoice.object;
    if (Array.isArray(technician_id)) {
      return technician_id.filter(Boolean).length > 0;
    }
    return Boolean(technician_id);
  }

  validate() {
    const { object } = this.props.invoiceStore.jobInvoice;
    const errors = {};
    if (object.client_id === '' || object.client_id === null || object.client_id === undefined) {
      errors.client = 'This job has no customer to bill.';
    }
    if (this.flat_rate) {
      const { description, quantity, rate_per_unit } = this.customLine.attributes;
      if (!String(description || '').trim()) {
        errors.flatTitle = 'Say what the charge is for.';
      }
      if (!(parseFloat(quantity) > 0)) {
        errors.flatQuantity = 'Enter a quantity above 0.';
      }
      if (!(parseFloat(rate_per_unit) > 0)) {
        errors.flatPrice = 'Enter a price above 0.';
      }
    }
    if (!this.hasTechnician()) {
      errors.technician = 'Choose who is creating this invoice.';
    }
    return errors;
  }

  focusFirstError(errors) {
    const order = [
      ['flatTitle', 'job-invoice-flat-title'],
      ['flatQuantity', 'job-invoice-flat-quantity'],
      ['flatPrice', 'job-invoice-flat-price'],
      ['technician', 'job-invoice-created-by'],
      ['client', 'job-invoice-summary'],
    ];
    const first = order.find(([key]) => errors[key]);
    if (!first) {
      return;
    }
    setTimeout(() => {
      const element = document.getElementById(first[1]);
      if (!element) {
        return;
      }
      element.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const focusable = element.matches('input, button') ? element : element.querySelector('input, button');
      if (focusable) {
        focusable.focus();
      }
    }, 50);
  }

  createInvoice() {
    let { object } = this.props.invoiceStore.jobInvoice;
    let {
      jobStore:{
        materialLineItems
      }
    } = this.props;
    object.line_items = [];

    const errors = this.validate();
    this.errors = errors;
    if (Object.keys(errors).length > 0) {
      this.focusFirstError(errors);
      return;
    }

    const { subTotal, totalTax, grandTotal } = this.totals();

    if (!this.flat_rate) {
      object.materials =  _.filter(materialLineItems.object.line_items.materials, function(material) {
        return material.attributes.is_checked ? material: '';
      });

      object.labours =  _.filter(materialLineItems.object.line_items.labours, function(labor) {
        return labor.attributes.is_checked ? labor: '';
      });

      object.customs =  _.filter(materialLineItems.object.line_items.customs, function(custom) {
        return custom.attributes.is_checked ? custom: '';
      });
    }else{
      // The flat-rate line carries its own total and tax, as typed.
      this.customLine.attributes.total = subTotal;
      this.customLine.attributes.tax_amount = totalTax;
      object.materials = []
      object.labours = []
      object.customs = []
      object.customs[0] = toJS(this.customLine)
    }

    object.flat_rate = this.flat_rate;
    object.grand_total = grandTotal;
    object.sub_total = subTotal;

    object.description = materialLineItems.object.line_items.descriptions.map(x => x).join('\n')

    this.props.invoiceStore
      .createJobInvoice()
      .then(response => {
        this.props.onClose();
        Toast.show({
          icon: "thumbs-up",
          message: "Invoice created successfully!",
          intent: Intent.SUCCESS
        });
      })
      .catch(error => {
        Toast.show({
          icon: "warning-sign",
          message: "Unable to create invoice!",
          intent: Intent.DANGER
        });
      });
  }

  handleCloseInvoiceDailog() {
    this.errors = {};
    this.props.onClose();
  }

  onTechnicianChange(technicians) {
    let {
      props: {
        invoiceStore: {
          jobInvoice: { object }
        }
      }
    } = this;
    let selectedValues = technicians.map(status => status.value);
    object.technician_id = [selectedValues[0]];
    if (selectedValues.length > 0) {
      this.errors = { ...this.errors, technician: null };
    }
  }

  onJobSelect(selectedOptions, allOptions) {
    let {
      props: {
        invoiceStore: {
          jobInvoice: { object }
        }
      }
    } = this;
    this.selectedOptions = selectedOptions;
    object.selectedJob = [selectedOptions[0]];
    object.scheduled_event_id = selectedOptions[0].value;
  }

  // Include or leave out one line item. Labor and materials get their marked-up
  // rate when included (from the original rate, so toggling doesn't compound it).
  setChecked(key, item, checked) {
    const attributes = item.attributes;
    attributes.is_checked = checked;
    if (key === 'labours' || key === 'materials') {
      attributes.original_rate_per_unit = attributes.original_rate_per_unit || attributes.rate_per_unit;
      attributes.rate_per_unit = attributes.markup
        ? (attributes.original_rate_per_unit * parseFloat(attributes.markup)).toFixed(2)
        : attributes.original_rate_per_unit;
    }
  }

  setAll(checked) {
    GROUPS.forEach(({ key }) => {
      this.visibleItems(key).forEach((item) => {
        if (Boolean(item.attributes.is_checked) !== checked) {
          this.setChecked(key, item, checked);
        }
      });
    });
  }

  setItemTax(item, taxId) {
    const taxLineItem = this.findTax(taxId);
    if (taxLineItem && taxLineItem.attributes) {
      item.attributes.tax_line_item_id = taxLineItem.attributes.id;
      item.attributes.tax_amount = (taxLineItem.attributes.percentage_value / 100) * item.attributes.total;
    } else {
      item.attributes.tax_line_item_id = '';
      item.attributes.tax_amount = 0;
    }
  }

  setFlatRate(flat) {
    const lineItems = this.lineItems;
    const { objects } = this.props.taxStore.taxes;
    if (flat === this.flat_rate) {
      return;
    }
    if (flat) {
      if (this.customLine.attributes.tax_line_item_id === '') {
        const defaultTax = _.find(objects, { attributes: { is_default: true } });
        if (defaultTax) {
          this.customLine.attributes.tax_line_item_id = defaultTax.attributes.id;
          this.customLine.attributes.tax_amount = 0;
        }
      }
      if (lineItems) {
        lineItems.labours.forEach((labor) => { labor.attributes.is_checked = false; });
        lineItems.materials.forEach((material) => { material.attributes.is_checked = false; });
        lineItems.customs.forEach((custom) => { custom.attributes.is_checked = false; });
      }
    } else {
      this.customLine = blankCustomLine();
    }
    this.errors = {};
    this.flat_rate = flat;
  }

  setCustomLine(field, value, errorKey) {
    this.customLine.attributes[field] = value;
    if (field === 'rate_per_unit') {
      this.customLine.attributes.markup = 1;
    }
    if (errorKey && this.errors[errorKey]) {
      this.errors = { ...this.errors, [errorKey]: null };
    }
  }

  onChangeDescription(event){
    let {
      jobStore:{
        materialLineItems:{
          object
        }
      }
    } = this.props;
    object.line_items.descriptions = []
    object.line_items.descriptions.push(event.target.value)
  }

  renderTaxSelect(id, value, onChange, disabled, label) {
    const { taxes } = this.props.taxStore;
    return (
      <div className="bp3-html-select bp3-fill">
        <select id={id} aria-label={label} value={value || ''} onChange={onChange} disabled={disabled}>
          <option value=''>No tax</option>
          {taxes.objects.map((tenantTax) => (
            <option value={tenantTax.attributes.id} key={tenantTax.attributes.id}>
              {tenantTax.attributes.name} {tenantTax.attributes.percentage_value}%
            </option>
          ))}
        </select>
      </div>
    );
  }

  renderItem(group, item) {
    const { attributes } = item;
    const checked = attributes.is_checked === true;
    const checkboxId = `job-invoice-${group.key}-${item.id}`;
    const description = attributes.description || group.title;
    const quantity = attributes.quantity;
    const meta = group.key === 'customs'
      ? null
      : `${quantity}${group.unit ? ` ${group.unit}` : ''} × ${currencyFormattedAmount(attributes.rate_per_unit)}`;
    return (
      <div key={item.id} style={styles.row(checked)}>
        <div style={styles.rowMain}>
          <Checkbox
            id={checkboxId}
            checked={checked}
            label={<strong>{description}</strong>}
            onChange={(event) => this.setChecked(group.key, item, event.target.checked)}
          />
          {meta && <span style={styles.rowMeta}>{meta}</span>}
        </div>
        <div style={styles.rowTaxSelect}>
          {this.renderTaxSelect(
            `${checkboxId}-tax`,
            attributes.tax_line_item_id,
            (event) => this.setItemTax(item, event.target.value),
            !checked,
            `Tax on ${description}`
          )}
        </div>
        <div style={styles.rowAmount}>
          <span style={styles.rowTotal}>{currencyFormattedAmount(attributes.total)}</span>
          <span style={styles.rowTax}>
            {toNumber(attributes.tax_amount) ? `+ ${currencyFormattedAmount(attributes.tax_amount)} tax` : 'No tax'}
          </span>
        </div>
      </div>
    );
  }

  renderItems() {
    const lineItems = this.lineItems;
    if (!lineItems) {
      return <p style={styles.empty}>Loading this job's labor and materials…</p>;
    }
    const groups = GROUPS.map((group) => ({ group, items: this.visibleItems(group.key) })).filter(({ items }) => items.length > 0);
    if (groups.length === 0) {
      return (
        <p style={styles.empty}>
          This job has no labor, materials or other charges yet. Choose <strong>Flat rate</strong> to bill a fixed amount.
        </p>
      );
    }
    return groups.map(({ group, items }) => {
      const included = items.filter((item) => item.attributes.is_checked).length;
      return (
        <div key={group.key} role="group" aria-label={group.title} style={{ marginBottom: 12 }}>
          <div style={styles.groupTitle}>
            <span>{group.title}</span>
            <span>{included} of {items.length}</span>
          </div>
          {items.map((item) => this.renderItem(group, item))}
        </div>
      );
    });
  }

  renderFlatRate() {
    const { attributes } = this.customLine;
    const errors = this.errors;
    return (
      <div>
        <FormGroup
          className={errors.flatTitle ? 'has-error' : undefined}
          label="What it's for"
          labelFor="job-invoice-flat-title"
          labelInfo={<span className="fb-required">*</span>}
        >
          <input
            id="job-invoice-flat-title"
            type="text"
            className="bp3-input bp3-fill"
            placeholder="e.g. AC repair, parts and labor"
            value={attributes.description}
            onChange={(event) => this.setCustomLine('description', event.target.value, 'flatTitle')}
          />
          <FieldError>{errors.flatTitle}</FieldError>
        </FormGroup>
        <div style={styles.flatGrid}>
          <FormGroup
            className={errors.flatQuantity ? 'has-error' : undefined}
            label="Quantity"
            labelFor="job-invoice-flat-quantity"
            labelInfo={<span className="fb-required">*</span>}
          >
            <input
              id="job-invoice-flat-quantity"
              type="number"
              min="0"
              step="any"
              inputMode="decimal"
              className="bp3-input bp3-fill"
              placeholder="e.g. 1"
              value={attributes.quantity}
              onChange={(event) => this.setCustomLine('quantity', event.target.value, 'flatQuantity')}
            />
            <FieldError>{errors.flatQuantity}</FieldError>
          </FormGroup>
          <FormGroup
            className={errors.flatPrice ? 'has-error' : undefined}
            label="Price each"
            labelFor="job-invoice-flat-price"
            labelInfo={<span className="fb-required">*</span>}
          >
            <input
              id="job-invoice-flat-price"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              className="bp3-input bp3-fill"
              placeholder="e.g. 450.00"
              value={attributes.rate_per_unit}
              onChange={(event) => this.setCustomLine('rate_per_unit', event.target.value, 'flatPrice')}
            />
            <FieldError>{errors.flatPrice}</FieldError>
          </FormGroup>
          <FormGroup label="Tax" labelFor="job-invoice-flat-tax">
            {this.renderTaxSelect(
              'job-invoice-flat-tax',
              attributes.tax_line_item_id,
              (event) => {
                const tax = this.findTax(event.target.value);
                attributes.tax_line_item_id = tax && tax.attributes ? tax.attributes.id : '';
              },
              false,
              'Tax'
            )}
          </FormGroup>
        </div>
      </div>
    );
  }

  render() {
    let {
      invoiceStore: {
        jobInvoice
      },
      jobStore:{
        materialLineItems:{
          object
        }
      },
      job,
      viewStore: { currentUser }
    } = this.props;
    let jobPoNumber = job && job.attributes && job.attributes.po_number;
    const busy = Boolean(jobInvoice.isLoading);
    const close = this.handleCloseInvoiceDailog.bind(this);
    const errors = this.errors;
    const { subTotal, totalTax, grandTotal, count, available } = this.totals();
    const client = job.attributes.client || {};
    const technicianIds = Array.isArray(jobInvoice.object.technician_id) ? jobInvoice.object.technician_id : [];
    const createdBySomeoneElse = Boolean(currentUser && technicianIds[0] && String(technicianIds[0]) !== String(currentUser.id));

    return (
      <FormDrawer
        isOpen={this.props.isOpen}
        title={`Create invoice for job #${job.attributes.event_number}`}
        icon="dollar"
        tone="billing"
        onClose={close}
        busy={busy}
        size="min(820px, 100vw)"
        footer={
          <>
            <Button text="Cancel" onClick={close} disabled={busy} />
            <Button
              intent={Intent.PRIMARY}
              loading={busy}
              onClick={this.createInvoice.bind(this)}
              icon="add"
            >
              Create invoice for {currencyFormattedAmount(grandTotal)}
            </Button>
          </>
        }
      >
        <div id="job-invoice-summary" className={errors.client ? 'has-error' : undefined} style={styles.summary}>
          <div>
            <p style={styles.summaryName}>{client.name || 'No customer'}</p>
            {client.ship_to && <p style={styles.summaryLine}>{client.ship_to}</p>}
            {(client.phone || client.email) && (
              <p style={styles.summaryLine}>{[client.phone, client.email].filter(Boolean).join(' · ')}</p>
            )}
            <FieldError>{errors.client}</FieldError>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={styles.summaryName}>Job #{job.attributes.event_number}</p>
            {job.attributes.title && <p style={styles.summaryLine}>{job.attributes.title}</p>}
          </div>
        </div>

        <FormSection
          title="What to bill"
          hint={this.flat_rate
            ? "One line for a fixed price instead of the job's items."
            : "Tick the labor, materials and charges from this job to put on the invoice."}
          aside={!this.flat_rate && available > 0 ? (
            <div style={{ whiteSpace: 'nowrap' }}>
              <button type="button" style={styles.linkButton} onClick={() => this.setAll(true)} disabled={count === available}>
                Select all
              </button>
              <span aria-hidden="true" style={{ color: 'var(--fd-faint)' }}> · </span>
              <button type="button" style={styles.linkButton} onClick={() => this.setAll(false)} disabled={count === 0}>
                None
              </button>
            </div>
          ) : null}
        >
          <Choice
            label="Bill"
            name="How to bill"
            value={this.flat_rate ? 'flat' : 'items'}
            onChange={(value) => this.setFlatRate(value === 'flat')}
            options={[
              { value: 'items', label: 'Items from this job' },
              { value: 'flat', label: 'Flat rate', hint: 'One fixed-price line; clears the ticked items' },
            ]}
          />

          {this.flat_rate ? this.renderFlatRate() : this.renderItems()}

          <div style={styles.totals} aria-live="polite">
            {!this.flat_rate && (
              <div style={styles.totalRow}>
                <span>Items included</span>
                <span>{count} of {available}</span>
              </div>
            )}
            <div style={styles.totalRow}>
              <span>Subtotal</span>
              <strong>{currencyFormattedAmount(subTotal)}</strong>
            </div>
            <div style={styles.totalRow}>
              <span>Tax</span>
              <span>{currencyFormattedAmount(totalTax)}</span>
            </div>
            <div style={styles.grandRow}>
              <span>Total</span>
              <span>{currencyFormattedAmount(grandTotal)}</span>
            </div>
          </div>
        </FormSection>

        <FormSection title="On the invoice" hint="Shown to the customer.">
          <FormGroup label="PO number" labelFor="job-invoice-po-number">
            <input
              id="job-invoice-po-number"
              type="text"
              className="bp3-input bp3-fill"
              placeholder="e.g. PO-20417"
              value={jobInvoice.object.po_number || jobPoNumber || ''}
              onChange={event => {
                jobInvoice.object.po_number = event.target.value || null;
              }}
            />
          </FormGroup>
          <FormGroup label="Notes for the customer" labelFor="job-invoice-notes">
            <GrowingTextArea
              id="job-invoice-notes"
              placeholder="e.g. Replaced the capacitor; system cooling normally."
              fill={true}
              onChange={this.onChangeDescription.bind(this)}
              value={(object && object.line_items && object.line_items.descriptions && object.line_items.descriptions.map(x => x).join('\n')) || ''}
            />
          </FormGroup>
          <FormGroup label="Payment terms" labelFor="job-invoice-payment-terms">
            <GrowingTextArea
              id="job-invoice-payment-terms"
              placeholder="e.g. Due within 30 days."
              fill={true}
              value={jobInvoice.object.payment_terms || ''}
              onChange={event => {
                jobInvoice.object.payment_terms = event.target.value;
              }}
            />
          </FormGroup>
        </FormSection>

        <MoreOptions count={2} open={Boolean(errors.technician) || createdBySomeoneElse}>
          <FormGroup label="Job" helperText="The job this invoice belongs to.">
            <JobPicker
              selectedOptions={toJS(jobInvoice.object.selectedJob)}
              onChange={this.onJobSelect.bind(this)}
              maximumSelections={1}
            />
          </FormGroup>
          <FormGroup
            className={errors.technician ? 'has-error' : undefined}
            label="Created by"
            labelInfo={<span className="fb-required">*</span>}
            helperText={errors.technician ? undefined : "You, unless you're making it for someone else."}
          >
            <div id="job-invoice-created-by">
              <TechnicianPicker
                fill="true"
                selectedOptions={jobInvoice.object.technician_id || ""}
                onChange={this.onTechnicianChange.bind(this)}
                includeAdmins={true}
                showCreatedBy={true}
                maximumSelections={1}
              />
            </div>
            <FieldError>{errors.technician}</FieldError>
          </FormGroup>
        </MoreOptions>
      </FormDrawer>
    );
  }
}

export default InvoiceJobModal;
